/** Canonical destinations for retired or relocated portal routes. */
const PORTAL_REDIRECTS: Readonly<Record<string, string>> = {
  "/billing": "/usage",
  "/settings/billing": "/usage",
  "/settings/branding": "/settings",
};

export function resolvePortalRedirect(pathname: string): string | null {
  return (
    PORTAL_REDIRECTS[pathname] ??
    resolveTeamRedirect(pathname) ??
    resolveRetiredSpecialistChatRedirect(pathname) ??
    resolveRetiredAssetDetailRedirect(pathname)
  );
}

/**
 * A dashboard and an internal application render only in the chromeless asset
 * view, so their retired detail URLs land on that view. Their retired settings
 * children land on the catalogue where editing now lives:
 *
 *   /dashboards/:id                             → /view/dashboard/:id
 *   /dashboards/:id/settings                    → /dashboards
 *   /applications/internal/:appId               → /view/application/:appId
 *   /applications/internal/:appId/settings      → /applications
 */
function resolveRetiredAssetDetailRedirect(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 3 && segments[0] === "dashboards" && segments[2] === "settings") {
    return segments[1] && reencodeSegment(segments[1]) !== null ? "/dashboards" : null;
  }
  if (
    segments.length === 4 &&
    segments[0] === "applications" &&
    segments[1] === "internal" &&
    segments[3] === "settings"
  ) {
    return segments[2] && reencodeSegment(segments[2]) !== null ? "/applications" : null;
  }
  if (segments.length === 2 && segments[0] === "dashboards" && segments[1]) {
    const dashboardId = reencodeSegment(segments[1]);
    return dashboardId === null ? null : `/view/dashboard/${dashboardId}`;
  }
  if (segments.length === 3 && segments[0] === "applications" && segments[1] === "internal") {
    const appId = segments[2] ? reencodeSegment(segments[2]) : null;
    return appId === null ? null : `/view/application/${appId}`;
  }
  // External applications keep their in-shell page because they have no
  // chromeless renderer.
  return null;
}

function resolveTeamRedirect(pathname: string): string | null {
  if (pathname === "/team" || pathname === "/team/") return "/settings/people-and-roles";
  if (!pathname.startsWith("/team/")) return null;
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length !== 2) return null;
  const userId = reencodeSegment(segments[1]);
  return userId === null ? null : `/settings/people-and-roles/${userId}`;
}

/**
 * The standalone specialist chat + conversation-transcript surfaces were
 * retired into Workspace, so their shipped URLs land on the equivalent
 * Workspace conversation instead of dead-ending:
 *
 *   /specialists/:id/chat                     → /workspace/:id
 *   /specialists/:id/chat/:sessionId          → /workspace/:id/:sessionId
 *   /specialists/:id/conversations/:sessionId → /workspace/:id/:sessionId
 *
 * The bare `/specialists/:id/conversations` index is kept, so it is not
 * redirected; every other specialist path (edit, tabs, permission groups) stays
 * put.
 */
function resolveRetiredSpecialistChatRedirect(pathname: string): string | null {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "specialists" || segments.length > 4) return null;
  const agentId = segments[1];
  const kind = segments[2];
  const sessionId = segments[3];
  if (!agentId || !kind) return null;

  if (kind === "chat") {
    if (segments.length === 3) return workspaceRedirectTarget(agentId);
    if (segments.length === 4 && sessionId) return workspaceRedirectTarget(agentId, sessionId);
    return null;
  }
  if (kind === "conversations" && segments.length === 4 && sessionId) {
    return workspaceRedirectTarget(agentId, sessionId);
  }
  return null;
}

/** Rebuilds a Workspace URL, re-encoding each captured (already-encoded) segment. */
function workspaceRedirectTarget(agentId: string, sessionId?: string): string | null {
  const agent = reencodeSegment(agentId);
  if (agent === null) return null;
  if (sessionId === undefined) return `/workspace/${agent}`;
  const session = reencodeSegment(sessionId);
  if (session === null) return null;
  return `/workspace/${agent}/${session}`;
}

function reencodeSegment(segment: string): string | null {
  try {
    return encodeURIComponent(decodeURIComponent(segment));
  } catch {
    return null;
  }
}
